import { getTypeColor, getTypeIcon } from '@/utils/nodeTypeMapper';
import {
  Avatar,
  Badge,
  Box,
  LinearProgress,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DataGridPremium,
  GRID_AGGREGATION_FUNCTIONS,
  useGridApiRef,
  useKeepGroupedColumnsHidden,
} from '@mui/x-data-grid-premium';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DataGridNoRowsCustomOverlay from '@/components/datagrid/DataGridNoRowsOverlay';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';

import { formatNumber } from '@/utils/formatters';
import { useSelector } from 'react-redux';
import { selectTimeRange } from '@/features/energyxplorer/redux/timeRangeSlice';
import { isOutOfBounds } from '@/features/energyxplorer/utils/timestampsSummaryHelpers';

const SeuNodeDataGrid = ({
  energyDistributionSummary,
  energyOverviewDataResult,
  handleSEURowClick,
}) => {
  const timeRange = useSelector(selectTimeRange);

  const apiRef = useGridApiRef();
  const { t } = useTranslation('energyxplorer-energy-overview-tab');

  const [sortModel, setSortModel] = useState([
    { field: 'consumption', sort: 'desc' },
  ]);

  const initialState = useKeepGroupedColumnsHidden({
    apiRef,
    initialState: {},
  });
  const typesToExclude = ['IkkeAnalyseret', 'Analyseret'];

  const columns = [
    {
      field: 'icon',
      headerName: t('data_grid_seu_columns.consumers.header'),
      width: 150,
      renderCell: (params) => {
        const shouldRenderWarning =
          !typesToExclude.includes(params.row.name) &&
          isOutOfBounds(params.row.timestampsSummary, timeRange);
        const content = (
          <Avatar
            variant="round"
            size="small"
            sx={{
              backgroundColor: getTypeColor(params.value) || '#555',
              color: '#fff',
              fontWeight: 'bold',
              height: 32,
              width: 32,
              fontSize: 16,
            }}
          >
            <i className={`fas ${getTypeIcon(params.value)}`}></i>
          </Avatar>
        );
        return (
          <Box display="flex" alignItems="center" width="100%" height="100%">
            {content}
            {shouldRenderWarning && (
              <Tooltip
                placement="top"
                title={t('data_grid_seu_columns.timestamp_summary_warning')}
              >
                <Badge
                  badgeContent={
                    <WarningRoundedIcon fontSize="small" color="warning" />
                  }
                  sx={{ ml: 1 }}
                >
                  <CalendarTodayRoundedIcon fontSize="small" />
                </Badge>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'name',
      headerName: t('data_grid_seu_columns.type.header'),
      width: 300,
      renderCell: (params) => {
        return (
          <>
            {params.row.id === 'Analyseret' ? (
              <Box
                display="flex"
                alignItems="center"
                width="100%"
                height="100%"
              >
                <Typography sx={{ marginLeft: 1 }}>
                  Neddelt SEU-forbrug i alt
                </Typography>
              </Box>
            ) : (
              <Box
                display="flex"
                alignItems="center"
                width="100%"
                height="100%"
              >
                <Typography sx={{ marginLeft: 1 }}>{params.value}</Typography>
              </Box>
            )}
          </>
        );
      },
    },
    {
      field: 'consumption',
      headerName: t('data_grid_seu_columns.consumption.header'),
      flex: 1,
      minWidth: 150,
      type: 'number',
      groupable: false,
      aggregable: true,
      headerAlign: 'left',

      aggregationFn: (params) => {
        return params.values.reduce((sum, val) => sum + (val || 0), 0);
      },

      valueFormatter: (params) => {
        return params.value != null ? `${params.value} kWh` : '-';
      },

      renderCell: (params) => {
        const { value } = params;
        return (
          <>
            {params.row.id === 'Analyseret' ? (
              <Box
                display="flex"
                alignItems="center"
                width="100%"
                height="100%"
              >
                <Typography>{formatNumber(params.value)} kWh</Typography>
              </Box>
            ) : (
              <Box
                display="flex"
                alignItems="center"
                width="100%"
                height="100%"
              >
                <Typography>
                  {value != null
                    ? `${formatNumber(value.toFixed(0))} kWh`
                    : '-'}
                </Typography>
              </Box>
            )}
          </>
        );
      },
    },
    {
      field: 'progress',
      headerName: t('data_grid_seu_columns.share.header'),
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const shouldRenderHashedProgress =
          (!typesToExclude.includes(params.row.name) &&
            isOutOfBounds(params.row.timestampsSummary, timeRange)) ||
          isOutOfBounds(energyOverviewDataResult.TimestampsSummary, timeRange);
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center', // Vertically center the content
              // justifyContent: 'center',
              // cursor: 'pointer',
              height: '100%', // Ensure the Box takes the full height of the cell
            }}
          >
            <>
              {shouldRenderHashedProgress ? (
                <Tooltip
                  title={t(
                    'data_grid_seu_columns.measured_percentage_incorrect_warning',
                  )}
                >
                  <LinearProgress
                    variant="determinate"
                    value={
                      params.row.contributionPercentage
                        ? params.row.contributionPercentage
                        : 0
                    }
                    sx={{
                      height: 15,
                      minWidth: 150,
                      borderRadius: 1,
                      overflow: 'hidden',

                      // Engraved track
                      backgroundImage: `repeating-linear-gradient(
      37deg,
      ${getTypeColor(params.row.id)}22,
      ${getTypeColor(params.row.id)}22 6px,
      transparent 6px,
      transparent 12px
    )`,
                      backgroundColor: `${getTypeColor(params.row.id)}14`,

                      '& .MuiLinearProgress-bar': {
                        // Engraved fill
                        backgroundImage: `repeating-linear-gradient(
        37deg,
        ${getTypeColor(params.row.id)},
        ${getTypeColor(params.row.id)} 6px,
        transparent 6px,
        transparent 12px
      )`,
                        opacity: 0.6,
                        transition: 'all 0.5s ease-in-out',
                      },
                    }}
                  />
                </Tooltip>
              ) : (
                <LinearProgress
                  variant="determinate"
                  value={
                    params.row.contributionPercentage
                      ? params.row.contributionPercentage
                      : 0
                  }
                  sx={{
                    height: 15,
                    minWidth: 150,
                    borderRadius: 1,
                    backgroundColor: `${getTypeColor(params.row.id)}33`,
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: getTypeColor(params.row.id),
                    },
                    transition: 'all 0.5s ease-in-out',
                  }}
                />
              )}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ ml: 1 }}
              >
                {typeof params.row.contributionPercentage === 'number'
                  ? `${params.row.contributionPercentage.toFixed(1)}%`
                  : '0.0%'}
              </Typography>
            </>
          </Box>
        );
      },
    },
    // actionColumn,
  ];

  const rows = useMemo(() => {
    if (!energyDistributionSummary) return [];
    const baseRows = energyDistributionSummary
      .filter((r) => r.Name !== 'IkkeAnalyseret')
      .map(
        ({
          Name,
          TotalEnergyUsage,
          EnergyContributionPercentage,
          TimestampsSummary,
        }) => {
          return {
            id: Name,
            icon: Name,
            name: Name,
            consumption: TotalEnergyUsage || 0,
            contributionPercentage: EnergyContributionPercentage || 0,
            unit: 'kWh',
            timestampsSummary: TimestampsSummary,
          };
        },
      );

    return [...baseRows];
  }, [energyDistributionSummary]);

  return (
    <DataGridPremium
      apiRef={apiRef}
      rows={rows}
      columns={columns}
      hideFooter
      pagination
      initialState={initialState}
      sortModel={sortModel}
      onSortModelChange={(model) => setSortModel(model)}
      sortingMode="client"
      disableRowSelectionOnClick
      onRowDoubleClick={(params) => {
        handleSEURowClick(params.row.id);
      }}
      getRowClassName={(params) =>
        params.id === 'Analyseret' || params.id === 'unknown'
          ? 'pinned-unknown-row'
          : ''
      }
      pinnedRows={{
        bottom: rows.filter((r) => r.id === 'Analyseret'),
      }}
      aggregationFunctions={{
        ...GRID_AGGREGATION_FUNCTIONS,
      }}
      groupingColDef={{ headerName: 'Group', width: 120 }}
      slots={{
        noRowsOverlay: DataGridNoRowsCustomOverlay,
      }}
      slotProps={{
        noRowsOverlay: {
          rows: t('data_grid_seu_columns.no_rows_overlay'),
          isLoading: false,
        },
      }}
      sx={{
        // flexGrow: 1,
        mt: 2,
        borderRadius: 2,
        borderColor: 'divider',

        // overflow: 'auto',
        // minHeight: 250,
        maxHeight: 400,
        '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 'bold' },
        '& .MuiDataGrid-footerContainer': {
          backgroundColor: (theme) =>
            theme.palette.mode === 'light' ? '#fff' : '#121212',
          borderTop: '1px solid',
          borderColor: 'divider',
        },
        '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within':
          {
            outline: 'none',
          },
        // '& .pinned-unknown-row .MuiDataGrid-cell': {
        //   backgroundColor: (theme) =>
        //     theme.palette.mode === 'light' ? '#fff5f5' : 'hsl(0, 20%, 15%)',
        //   fontWeight: 'bold',
        //   // color: '#d32f2f',
        //   color: (theme) =>
        //     theme.palette.mode === 'light' ? '#d32f2f' : '#ffb3b3',
        // },
        // '& .MuiDataGrid-pinnedRowsBottom .MuiDataGrid-row': {
        //   boxShadow: 'inset 0 1px 0 #e0e0e0',
        // },
        '& .MuiDataGrid-main': {
          backgroundColor: (theme) =>
            theme.palette.mode === 'light' ? '#fff' : '#121212',
        },
      }}
    />
  );
};

export default SeuNodeDataGrid;
